(function () {
  if (customElements.get('collection-infinite-scroll')) {
    return;
  }

  class CollectionInfiniteScroll extends HTMLElement {
    connectedCallback() {
      this.queue = [];
      this.fetching = false;
      this.stepping = false;
      this.cardsPerLoad = parseInt(this.getAttribute('cards-per-load'), 10) || 12;
      this.sectionId = this.getAttribute('section-id') || null;
      this.nextUrl = this.getAttribute('data-next-url') || null;
      this.abortController = null;
      this.productList = document.getElementById(this.getAttribute('product-list-id'));
      this.sentinel = this.querySelector('.collection-infinite-scroll__sentinel');
      this.status = this.querySelector('.collection-infinite-scroll__status');
      this.onScroll = this.onScroll.bind(this);
      this.onPageHide = this.onPageHide.bind(this);

      // With JS available, hide the numbered pagination. It stays in the markup as the
      // no-JS and SEO fallback, and as the source of the next page URL.
      this.pagination = this.closest('.collection__main')
        ? this.closest('.collection__main').querySelector('.pagination')
        : null;
      if (this.pagination) {
        this.pagination.style.display = 'none';
      }

      if (!this.productList || !this.sentinel) {
        return;
      }

      // The first page is server-rendered with products_per_page PRODUCTS, which the color split
      // expands into many cards. Trim that initial render down to one card budget and move the rest
      // into the buffer, so the visible count is controlled from the first paint, not only on later
      // loads. The buffered cards are drained on scroll like fetched ones.
      const overflow = Array.prototype.slice.call(this.productList.children, this.cardsPerLoad);
      overflow.forEach(function (card) {
        this.productList.removeChild(card);
        this.queue.push(card);
      }, this);

      // The CSS budget gate (in the section stylesheet) prevents a first-paint flash; once JS owns
      // the count it must go, otherwise it would also hide cards appended later.
      this.productList.classList.remove('is-budget-gated');

      window.addEventListener('pagehide', this.onPageHide);

      // Nothing left to load lazily (everything already fits the budget and there is no next page).
      if (this.queue.length === 0 && !this.nextUrl) {
        return;
      }

      this.observer = new IntersectionObserver(this.onIntersect.bind(this), { rootMargin: '600px 0px' });

      // When the visitor arrives via the browser back/forward button, rebuild and jump to where
      // they were instead of starting over at the top.
      if (this.restoreFromHistory()) {
        return;
      }

      this.observer.observe(this.sentinel);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
      }
      if (this.abortController) {
        this.abortController.abort();
      }
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('pagehide', this.onPageHide);
    }

    onIntersect(entries) {
      if (this.stepping || !entries.some(function (entry) { return entry.isIntersecting; })) {
        return;
      }
      this.runStep();
    }

    onScroll() {
      // Re-arm after the user has actually scrolled, so batches never load in a burst.
      if (this.isConnected && this.sentinel && this.observer) {
        this.observer.observe(this.sentinel);
      }
    }

    async runStep() {
      this.stepping = true;
      try {
        // Refill the buffer from the server only when it cannot satisfy one full batch.
        if (this.queue.length < this.cardsPerLoad && this.nextUrl) {
          await this.fetchNextPage();
        }

        // The section may have been replaced (e.g. by a filter change) during the fetch; if so,
        // this element is detached and must not touch the DOM or re-arm any listeners.
        if (!this.isConnected) {
          return;
        }

        if (this.queue.length === 0) {
          if (!this.nextUrl) {
            this.finish();
          }
          return;
        }

        // Append exactly one card budget, independent of how many colors each product has.
        this.appendBatch(this.cardsPerLoad);

        if (this.queue.length === 0 && !this.nextUrl) {
          this.finish();
          return;
        }

        // Gate the next batch behind a real scroll so short pages do not chain-load.
        this.observer.unobserve(this.sentinel);
        window.addEventListener('scroll', this.onScroll, { once: true, passive: true });
      } finally {
        this.stepping = false;
      }
    }

    appendBatch(limit) {
      const batch = this.queue.splice(0, limit);
      const fragment = document.createDocumentFragment();
      batch.forEach(function (card) {
        // The reveal animation only runs for cards present at first paint; drop the attribute so
        // appended cards are not stuck at opacity 0.
        card.removeAttribute('reveal-on-scroll');
        // Cards trimmed from the initial render belong to this document and are re-attached directly;
        // cards parsed from a fetched page live in another document and must be imported first.
        fragment.appendChild(card.ownerDocument === document ? card : document.importNode(card, true));
      });
      // One insertion for the whole batch, so the browser reflows once instead of once per card.
      this.productList.appendChild(fragment);
    }

    async fetchNextPage() {
      this.fetching = true;
      if (this.status) {
        this.status.hidden = false;
      }
      this.abortController = new AbortController();

      try {
        // Prefer the lighter Section Rendering response; fall back to the full page if it does not
        // contain the product list (e.g. if the store does not support section + page rendering).
        let doc = await this.fetchDoc(this.nextUrl, true);
        let newList = doc ? doc.getElementById(this.getAttribute('product-list-id')) : null;

        if (!newList && this.sectionId) {
          doc = await this.fetchDoc(this.nextUrl, false);
          newList = doc ? doc.getElementById(this.getAttribute('product-list-id')) : null;
        }

        // Only take the product cards, never the wrapping section elements, to avoid duplicate ids.
        if (newList) {
          this.queue.push.apply(this.queue, Array.prototype.slice.call(newList.children));
        }

        const nextLink = doc ? doc.querySelector('.pagination a[rel="next"]') : null;
        this.nextUrl = nextLink ? nextLink.getAttribute('href') : null;
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Network failure: stop and bring back the numbered pagination for manual navigation.
          this.nextUrl = null;
          if (this.pagination) {
            this.pagination.style.display = '';
          }
        }
      } finally {
        this.fetching = false;
        if (this.status) {
          this.status.hidden = true;
        }
        this.abortController = null;
      }
    }

    async fetchDoc(href, useSection) {
      const url = new URL(href, window.location.href);
      // The next link never carries section_id; strip defensively, then opt into section rendering.
      url.searchParams.delete('section_id');
      if (useSection && this.sectionId) {
        url.searchParams.set('section_id', this.sectionId);
      }

      const response = await fetch(url.toString(), { signal: this.abortController.signal });
      if (!response.ok) {
        throw new Error('Infinite scroll request failed: ' + response.status);
      }
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }

    // ---- Back/forward position restoration ----

    storageKey() {
      return 'infinite-scroll:' + window.location.pathname + window.location.search;
    }

    onPageHide() {
      try {
        const state = { y: window.scrollY, count: this.productList.children.length };
        sessionStorage.setItem(this.storageKey(), JSON.stringify(state));
      } catch (error) {
        // sessionStorage can be unavailable (private mode, quota); saving position is best-effort.
      }
    }

    restoreFromHistory() {
      let raw = null;
      try {
        raw = sessionStorage.getItem(this.storageKey());
        sessionStorage.removeItem(this.storageKey());
      } catch (error) {
        raw = null;
      }
      if (!raw) {
        return false;
      }

      let navType = null;
      try {
        const entry = performance.getEntriesByType('navigation')[0];
        navType = entry ? entry.type : null;
      } catch (error) {
        navType = null;
      }
      // Only restore on an actual back/forward navigation, never on a fresh visit.
      if (navType !== 'back_forward') {
        return false;
      }

      let state = null;
      try {
        state = JSON.parse(raw);
      } catch (error) {
        return false;
      }
      if (!state || !state.count || state.count <= this.productList.children.length) {
        return false;
      }

      this.fillTo(state.count, state.y || 0);
      return true;
    }

    async fillTo(targetCount, y) {
      // Rebuild the list to the previously loaded length without scroll gating, then jump back.
      while (this.productList.children.length < targetCount) {
        if (this.queue.length === 0) {
          if (!this.nextUrl) {
            break;
          }
          await this.fetchNextPage();
          if (!this.isConnected) {
            return;
          }
          if (this.queue.length === 0) {
            break;
          }
        }

        const remaining = targetCount - this.productList.children.length;
        this.appendBatch(remaining < this.cardsPerLoad ? remaining : this.cardsPerLoad);
      }

      window.scrollTo(0, y);

      if (this.nextUrl || this.queue.length > 0) {
        this.observer.observe(this.sentinel);
      } else {
        this.finish();
      }
    }

    finish() {
      if (this.observer) {
        this.observer.disconnect();
      }
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  customElements.define('collection-infinite-scroll', CollectionInfiniteScroll);
})();
